// Panel admin — boîte aux lettres des payloads clients rejetés définitivement.
// - Liste paginée + filtre "inclure résolus"
// - Modale de détail : voir le payload complet (JSON pretty-print), le corriger,
//   sauver la modification (utile si un dev veut rejouer manuellement) ou marquer résolu.
import React, { useEffect, useState, useCallback } from "react";
import {
	adminSyncFailuresList,
	adminSyncFailureUpdate,
	adminSyncFailureResolve,
} from "../services/authService";

function formatDateTime(v) {
	if (!v) return "—";
	const d = new Date(v);
	if (isNaN(d)) return "—";
	return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

function prettyJson(raw) {
	if (!raw) return "";
	try {
		return JSON.stringify(JSON.parse(raw), null, 2);
	} catch {
		return raw; // pas du JSON valide -> affiche tel quel
	}
}

// ─── Modale de détail / édition d'un failure ───
function FailureDetailModal({ failure, onClose, onSaved }) {
	const [payload, setPayload] = useState(prettyJson(failure.requestPayload));
	const [note, setNote] = useState(failure.resolvedNote || "");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	const handleSave = async () => {
		setSaving(true); setError("");
		const res = await adminSyncFailureUpdate(failure.id, payload, note);
		setSaving(false);
		if (res.success) onSaved?.();
		else setError(res.error || "Erreur");
	};

	const handleResolve = async () => {
		if (!window.confirm("Marquer ce failure comme traité ?")) return;
		setSaving(true); setError("");
		const res = await adminSyncFailureResolve(failure.id, note);
		setSaving(false);
		if (res.success) onSaved?.();
		else setError(res.error || "Erreur");
	};

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			<div className="mens-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900, width: "92%" }}>
				<div className="mens-modal-header">
					<h3>Failure #{failure.id}</h3>
					<button className="mens-modal-close" onClick={onClose}>×</button>
				</div>

				<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13, color: "var(--ink-soft)", marginTop: 10 }}>
					<div><strong>User :</strong> {failure.userName} <span style={{ opacity: 0.6 }}>({failure.userEmail})</span></div>
					<div><strong>Endpoint :</strong> <code>{failure.endpoint}</code></div>
					{failure.ownerId != null && (
						<div><strong>Owner (mode coach) :</strong> {failure.ownerName || `#${failure.ownerId}`}</div>
					)}
					<div><strong>Plateforme :</strong> {failure.platform} · v{failure.appVersion}</div>
					<div><strong>Statut HTTP :</strong> {failure.responseStatus ?? "—"} · <em>{failure.classification}</em></div>
					<div><strong>Tentatives :</strong> {failure.attempts}</div>
					<div><strong>Survenu :</strong> {formatDateTime(failure.occurredAt)}</div>
					<div><strong>Reçu :</strong> {formatDateTime(failure.receivedAt)}</div>
				</div>

				{failure.responseBody && (
					<div style={{ marginTop: 14 }}>
						<div style={{ fontSize: 12, color: "var(--ink-soft)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Réponse serveur</div>
						<pre className="sync-failure-pre" style={{ background: "rgba(244,67,54,0.06)", borderColor: "rgba(244,67,54,0.20)" }}>{failure.responseBody}</pre>
					</div>
				)}

				<div style={{ marginTop: 14 }}>
					<div style={{ fontSize: 12, color: "var(--ink-soft)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
						Payload envoyé (éditable pour rejouer)
					</div>
					<textarea
						className="sync-failure-textarea"
						value={payload}
						onChange={(e) => setPayload(e.target.value)}
						spellCheck={false}
					/>
				</div>

				<div style={{ marginTop: 12 }}>
					<label style={{ fontSize: 12, color: "var(--ink-soft)", textTransform: "uppercase", letterSpacing: 0.5 }}>Note (résolution / commentaire dev)</label>
					<textarea
						className="admin-broadcast-input"
						value={note}
						onChange={(e) => setNote(e.target.value)}
						rows={2}
						placeholder="Ex: fix commit abc123, replay OK"
					/>
				</div>

				{error && <div className="mens-modal-error">{error}</div>}

				<div className="mens-modal-actions">
					<button className="mens-btn ghost" onClick={onClose} disabled={saving}>Fermer</button>
					<button className="mens-btn ghost" onClick={handleSave} disabled={saving}>
						{saving ? "…" : "Sauver payload"}
					</button>
					{!failure.resolved && (
						<button className="mens-btn primary" onClick={handleResolve} disabled={saving}>
							{saving ? "…" : "Marquer résolu"}
						</button>
					)}
				</div>
			</div>
		</div>
	);
}

// ─── Panel principal ───
export default function AdminSyncFailuresPanel() {
	const [items, setItems] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [pageSize] = useState(30);
	const [includeResolved, setIncludeResolved] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [detailFailure, setDetailFailure] = useState(null);

	const load = useCallback(async () => {
		setLoading(true); setError("");
		const res = await adminSyncFailuresList({ page, pageSize, includeResolved });
		setLoading(false);
		if (res.success) {
			setItems(res.items || []);
			setTotal(res.total || 0);
			setTotalPages(res.totalPages || 1);
		} else {
			setError(res.error || "Erreur");
		}
	}, [page, pageSize, includeResolved]);

	useEffect(() => { load(); }, [load]);

	return (
		<div>
			<div className="admin-users-toolbar">
				<div className="admin-users-filters">
					<button
						type="button"
						className={`admin-filter-chip ${!includeResolved ? "selected" : ""}`}
						onClick={() => { setPage(1); setIncludeResolved(false); }}
					>
						À traiter
					</button>
					<button
						type="button"
						className={`admin-filter-chip ${includeResolved ? "selected" : ""}`}
						onClick={() => { setPage(1); setIncludeResolved(true); }}
					>
						Tous (avec résolus)
					</button>
				</div>
				<span style={{ color: "var(--ink-soft)", fontSize: 14 }}>
					{total} failure{total > 1 ? "s" : ""}
				</span>
			</div>

			{error && <div className="admin-error">{error}</div>}
			{loading && <div className="admin-loading">Chargement…</div>}

			{!loading && items.length === 0 && (
				<div className="admin-empty">Aucun failure à afficher.</div>
			)}

			{!loading && items.length > 0 && (
				<div className="admin-users-table-wrap">
					<table className="admin-users-table">
						<thead>
							<tr>
								<th>Reçu</th>
								<th>User</th>
								<th>Endpoint</th>
								<th>HTTP</th>
								<th>Classif.</th>
								<th>Tent.</th>
								<th>Statut</th>
								<th style={{ textAlign: "right" }}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{items.map((f) => (
								<tr
									key={f.id}
									className="clickable-row"
									onClick={() => setDetailFailure(f)}
								>
									<td style={{ whiteSpace: "nowrap" }}>{formatDateTime(f.receivedAt)}</td>
									<td>
										<div className="admin-user-name">{f.userName || `#${f.userId}`}</div>
										<div className="admin-user-email">{f.userEmail}</div>
									</td>
									<td><code style={{ fontSize: 12 }}>{f.endpoint}</code></td>
									<td>{f.responseStatus ?? "—"}</td>
									<td>
										<span className={`admin-source-tag source-${f.classification}`}>
											{f.classification || "?"}
										</span>
									</td>
									<td>{f.attempts}</td>
									<td>
										{f.resolved ? (
											<span style={{ color: "var(--ink-soft)", fontSize: 12 }}>✓ résolu</span>
										) : (
											<span style={{ color: "#e57373", fontSize: 12, fontWeight: 700 }}>À traiter</span>
										)}
									</td>
									<td
										style={{ textAlign: "right" }}
										onClick={(e) => e.stopPropagation()}
									>
										<button className="mens-btn ghost small" onClick={() => setDetailFailure(f)}>
											Ouvrir
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>

					<div className="admin-users-pagination">
						<span>Page {page} / {totalPages}</span>
						<div>
							<button className="mens-btn ghost small" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Précédent</button>
							<button className="mens-btn ghost small" disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ marginLeft: 6 }}>Suivant →</button>
						</div>
					</div>
				</div>
			)}

			{detailFailure && (
				<FailureDetailModal
					failure={detailFailure}
					onClose={() => setDetailFailure(null)}
					onSaved={() => { setDetailFailure(null); load(); }}
				/>
			)}
		</div>
	);
}
